// frontend/src/components/Dashboard/OrderDetailsModal.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faBox,
  faDollarSign,
  faTruck,
  faCalendar,
  faEdit,
  faCheckCircle,
  faClock,
  faExclamationCircle,
  faTractor,
  faCreditCard,
  faSpinner,
  faSave,
  faUndo,
  faInfoCircle,
  faClipboard,
  faReceipt,
  faShoppingCart,
  faWallet,
  faMobileAlt,
  faBuilding,
  faCity,
  faGlobe,
  faWeightScale,
  faBoxes,
  faTag,
  faPercentage,
  faShip,
  faAnchor,
  faWhatsapp,
  faLocationDot,
  faCheckDouble,
  faRocket,
  faExclamationTriangle,
  faShieldAlt,
  faUserCheck,
  faStore,
  faCrown,
  faUsers,
  faLeaf,
  faCow,
  faAddressCard,
  faReceipt as faReceiptIcon,
  faClipboardList,
  faMoneyBillWave,
  faMoneyBillTransfer,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";

// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    pending: "#D9A321",
    processing: "#3b82f6",
    confirmed: "#8b5cf6",
    shipped: "#8b5cf6",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  switch (status) {
    case "delivered":
      return faCheckDouble;
    case "cancelled":
      return faTimes;
    case "out_for_delivery":
      return faRocket;
    case "shipped":
      return faShip;
    default:
      return faClock;
  }
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "⏳ Pending",
    processing: "⚙️ Processing",
    confirmed: "✅ Confirmed",
    shipped: "🚢 Shipped",
    out_for_delivery: "📬 Out for Delivery",
    delivered: "📦 Delivered",
    cancelled: "❌ Cancelled",
    refunded: "↩️ Refunded",
  };
  return labels[status] || status;
};

const getPaymentMethodLabel = (method) => {
  const labels = {
    cash_on_delivery: "💵 Cash on Delivery",
    bank_transfer: "🏦 Bank Transfer",
    mobile_money: "📱 Mobile Money",
    credit: "💳 Credit",
    card: "💳 Credit/Debit Card",
    orange_money: "🟠 Orange Money",
    afrimoney: "📱 Afrimoney",
    wave: "🌊 WAVE",
  };
  return labels[method] || method?.replace(/_/g, " ") || "N/A";
};

const getPaymentStatusLabel = (status) => {
  const labels = {
    pending: "⏳ Pending",
    processing: "⚙️ Processing",
    paid: "✅ Paid",
    failed: "❌ Failed",
    refunded: "↩️ Refunded",
    deposit_received: "💵 Deposit Received",
  };
  return labels[status] || status || "N/A";
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

// ============================================
// MAIN COMPONENT
// ============================================
const OrderDetailsModal = ({ order, onClose, onUpdateStatus, isAdmin }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(order?.status || "");
  const [expandedSections, setExpandedSections] = useState({
    customer: true,
    shipping: true,
    items: true,
    timeline: true,
    payment: true,
    notes: true,
    farmer: false,
    supplier: true,
  });

  if (!order) return null;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleStatusUpdate = async () => {
    if (selectedStatus === order.status) {
      setEditing(false);
      return;
    }
    setLoading(true);
    try {
      await onUpdateStatus(order._id, selectedStatus);
      setEditing(false);
    } catch (error) {
      console.error("Error updating status:", error);
      alert(error.response?.data?.message || "Failed to update order status");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setSelectedStatus(order.status);
    setEditing(false);
  };

  const isBulkOrder =
    order.orderType === "bulk_farmer" ||
    (order.totalQuantity && order.totalQuantity >= 100);
  const isCreditOrder =
    order.payment?.method === "credit" ||
    order.farmerDetails?.creditTerms?.isCreditOrder;
  const isFromMalaysia =
    order.origin === SUPPLIER.country ||
    order.supplier?.country === SUPPLIER.country;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content order-details-modal large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faReceiptIcon} /> Order Details -{" "}
            {order.orderNumber}
            {isBulkOrder && (
              <span className="badge bulk">
                <FontAwesomeIcon icon={faTractor} /> BULK
              </span>
            )}
            {isCreditOrder && (
              <span className="badge credit">
                <FontAwesomeIcon icon={faCreditCard} /> Credit
              </span>
            )}
            {isFromMalaysia && (
              <span className="badge malaysia">
                <FontAwesomeIcon icon={faShip} /> {SUPPLIER.country}
              </span>
            )}
            {order.source && (
              <span className="badge source">
                <FontAwesomeIcon icon={faGlobe} /> {order.source}
              </span>
            )}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {/* ============================================
              STATUS SECTION
              ============================================ */}
          <div
            className="status-section"
            style={{
              borderLeft: `4px solid ${getStatusColor(order.status)}`,
              background: `${getStatusColor(order.status)}10`,
            }}
          >
            <div className="status-header">
              <div className="status-display">
                <FontAwesomeIcon
                  icon={getStatusIcon(order.status)}
                  style={{ color: getStatusColor(order.status) }}
                />
                <span
                  className="status-label"
                  style={{ color: getStatusColor(order.status) }}
                >
                  {getStatusLabel(order.status)}
                </span>
                <span className="order-date">
                  <FontAwesomeIcon icon={faCalendar} />
                  {formatDate(order.createdAt)}
                </span>
                {order.ageInDays > 0 && (
                  <span className="order-age">
                    {order.ageInDays} day{order.ageInDays > 1 ? "s" : ""} old
                  </span>
                )}
                {isFromMalaysia && (
                  <span className="origin-tag">
                    <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                  </span>
                )}
              </div>
              {isAdmin && (
                <div className="status-actions">
                  {!editing ? (
                    <button
                      className="btn-edit"
                      onClick={() => setEditing(true)}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Update Status
                    </button>
                  ) : (
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      <FontAwesomeIcon icon={faUndo} /> Cancel
                    </button>
                  )}
                </div>
              )}
            </div>

            {isAdmin && editing && (
              <div className="status-update-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="status-select"
                    >
                      <option value="pending">⏳ Pending</option>
                      <option value="processing">⚙️ Processing</option>
                      <option value="confirmed">✅ Confirmed</option>
                      <option value="shipped">🚢 Shipped</option>
                      <option value="out_for_delivery">
                        📬 Out for Delivery
                      </option>
                      <option value="delivered">📦 Delivered</option>
                      <option value="cancelled">❌ Cancelled</option>
                      <option value="refunded">↩️ Refunded</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button
                    className="btn-primary"
                    onClick={handleStatusUpdate}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Updating...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} /> Update Status
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ============================================
              SUPPLIER INFORMATION
              ============================================ */}
          <div className="detail-section supplier-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("supplier")}
            >
              <h3>
                <FontAwesomeIcon icon={faShip} /> Supplier Information
                <span className="supplier-badge">
                  <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.supplier ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.supplier && (
              <div className="supplier-details">
                <div className="supplier-info-card">
                  <FontAwesomeIcon icon={faShip} className="supplier-icon" />
                  <div className="supplier-info">
                    <strong>{SUPPLIER.name}</strong>
                    <span>
                      <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}{" "}
                      &bull;
                      <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
                    </span>
                  </div>
                  <span className="supplier-badge">
                    <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
                  </span>
                </div>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Origin:</span>
                    <span className="detail-value">
                      <span className="origin-tag">
                        <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}
                      </span>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Port of Loading:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Port of Discharge:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faAnchor} />{" "}
                      {SUPPLIER.portDischarge}
                    </span>
                  </div>
                  {order.shipping?.vesselName && (
                    <div className="detail-item">
                      <span className="detail-label">Vessel:</span>
                      <span className="detail-value">
                        <FontAwesomeIcon icon={faShip} />{" "}
                        {order.shipping.vesselName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ============================================
              CUSTOMER INFORMATION
              ============================================ */}
          <div className="detail-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("customer")}
            >
              <h3>
                <FontAwesomeIcon icon={faUser} /> Customer Information
              </h3>
              <span className="toggle-icon">
                {expandedSections.customer ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.customer && (
              <div className="customer-details">
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">
                      {order.userName ||
                        order.customerName ||
                        order.user?.name ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faEnvelope} /> Email:
                    </span>
                    <span className="detail-value">
                      {order.userEmail ||
                        order.customerEmail ||
                        order.user?.email ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faPhone} /> Phone:
                    </span>
                    <span className="detail-value">
                      <a
                        href={`tel:${order.userPhone || order.customerPhone || order.user?.phone}`}
                        className="phone-link"
                      >
                        {order.userPhone ||
                          order.customerPhone ||
                          order.user?.phone ||
                          "N/A"}
                      </a>
                    </span>
                  </div>
                  {order.customerWhatsapp && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp:
                      </span>
                      <span className="detail-value">
                        <a
                          href={`https://wa.me/${order.customerWhatsapp}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whatsapp-link"
                        >
                          <FontAwesomeIcon icon={faWhatsappBrand} />{" "}
                          {order.customerWhatsapp}
                        </a>
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">User Type:</span>
                    <span className={`user-type-badge ${order.userType}`}>
                      {order.userType === "farmer"
                        ? "🌾 Farmer"
                        : "🛒 Customer"}
                    </span>
                  </div>
                  {order.farmerDetails?.farmName && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faTractor} /> Farm:
                      </span>
                      <span className="detail-value">
                        {order.farmerDetails.farmName}
                      </span>
                    </div>
                  )}
                  {order.farmerDetails?.farmSize && (
                    <div className="detail-item">
                      <span className="detail-label">Farm Size:</span>
                      <span className="detail-value">
                        {order.farmerDetails.farmSize.charAt(0).toUpperCase() +
                          order.farmerDetails.farmSize.slice(1)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ============================================
              SHIPPING ADDRESS
              ============================================ */}
          <div className="detail-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("shipping")}
            >
              <h3>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address
              </h3>
              <span className="toggle-icon">
                {expandedSections.shipping ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.shipping && (
              <div className="shipping-details">
                {order.shippingAddress ? (
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Recipient:</span>
                      <span className="detail-value">
                        {order.shippingAddress.recipientName || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Address:</span>
                      <span className="detail-value">
                        {order.shippingAddress.street}
                        {order.shippingAddress.city &&
                          `, ${order.shippingAddress.city}`}
                        {order.shippingAddress.district &&
                          `, ${order.shippingAddress.district}`}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Country:</span>
                      <span className="detail-value">
                        <span className="destination-tag">
                          🇸🇱 {order.shippingAddress.country || "Sierra Leone"}
                        </span>
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faPhone} /> Phone:
                      </span>
                      <span className="detail-value">
                        {order.shippingAddress.phone || "N/A"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="no-data">No shipping address provided</p>
                )}
              </div>
            )}
          </div>

          {/* ============================================
              ORDER ITEMS
              ============================================ */}
          <div className="detail-section items-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("items")}
            >
              <h3>
                <FontAwesomeIcon icon={faBox} /> Order Items
                <span className="item-count">
                  ({order.totalItems || order.items?.length || 0} items,
                  {order.totalQuantity || 0} kg)
                </span>
                <span className="origin-summary">
                  <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.items ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.items && (
              <div className="table-responsive">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Unit Price</th>
                      <th>Discount</th>
                      <th>Origin</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items?.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <div className="product-info">
                            {item.image && (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="item-thumb"
                              />
                            )}
                            <span className="item-name">{item.name}</span>
                            {item.priceTier && item.priceTier !== "retail" && (
                              <span className="tier-badge">
                                {item.priceTier.replace("_", " ")}
                              </span>
                            )}
                            {item.origin === SUPPLIER.country && (
                              <span className="origin-tag">🇲🇾</span>
                            )}
                          </div>
                        </td>
                        <td>{item.quantity}</td>
                        <td>{item.unit || "kg"}</td>
                        <td>${(item.price?.usd || 0).toFixed(2)}</td>
                        <td>
                          {item.discountPercent > 0 ? (
                            <span className="discount-badge">
                              -{item.discountPercent}%
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <span className="origin-tag">
                            <FontAwesomeIcon icon={faGlobe} />{" "}
                            {item.origin || SUPPLIER.country}
                          </span>
                        </td>
                        <td>${(item.totalPrice?.usd || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="5"></td>
                      <td className="total-label">Subtotal:</td>
                      <td className="total-value">
                        ${(order.subtotal?.usd || 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="5"></td>
                      <td className="total-label">Shipping:</td>
                      <td className="total-value">
                        ${(order.shippingCost?.usd || 0).toFixed(2)}
                      </td>
                    </tr>
                    {order.discount?.usd > 0 && (
                      <tr>
                        <td colSpan="5"></td>
                        <td className="total-label discount">Discount:</td>
                        <td className="total-value discount">
                          -${(order.discount?.usd || 0).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {order.tax?.usd > 0 && (
                      <tr>
                        <td colSpan="5"></td>
                        <td className="total-label">Tax:</td>
                        <td className="total-value">
                          ${(order.tax?.usd || 0).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    <tr className="grand-total">
                      <td colSpan="5"></td>
                      <td className="total-label">
                        <strong>Total:</strong>
                      </td>
                      <td className="total-value">
                        <strong>
                          $
                          {(
                            order.total?.usd ||
                            order.totalAmount?.usd ||
                            0
                          ).toFixed(2)}
                        </strong>
                      </td>
                    </tr>
                    {order.currency === "SLL" && order.exchangeRate && (
                      <tr>
                        <td colSpan="5"></td>
                        <td className="total-label">Exchange Rate:</td>
                        <td className="total-value">
                          1 USD ={" "}
                          {order.exchangeRate?.usdToSll?.toLocaleString() ||
                            "N/A"}{" "}
                          SLL
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* ============================================
              ORDER TIMELINE
              ============================================ */}
          <div className="detail-section timeline-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("timeline")}
            >
              <h3>
                <FontAwesomeIcon icon={faCalendar} /> Order Timeline
                <span className="timeline-count">
                  ({order.timeline?.length || 1} events)
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.timeline ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.timeline && (
              <div className="timeline">
                {order.timeline?.length > 0 ? (
                  order.timeline.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div
                        className="timeline-dot"
                        style={{
                          background: getStatusColor(event.status),
                          borderColor:
                            index === order.timeline.length - 1
                              ? "#D9A321"
                              : getStatusColor(event.status),
                        }}
                      />
                      <div className="timeline-content">
                        <div className="timeline-status">
                          {getStatusLabel(event.status)}
                          {event.updatedBy && (
                            <span className="updated-by">
                              by {event.updatedBy}
                            </span>
                          )}
                        </div>
                        {event.description && (
                          <div className="timeline-description">
                            {event.description}
                          </div>
                        )}
                        <div className="timeline-date">
                          {formatDate(event.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="timeline-item">
                    <div
                      className="timeline-dot"
                      style={{ background: "#D9A321" }}
                    />
                    <div className="timeline-content">
                      <div className="timeline-status">Order Created</div>
                      <div className="timeline-date">
                        {formatDate(order.createdAt)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ============================================
              PAYMENT INFORMATION
              ============================================ */}
          {order.payment && (
            <div className="detail-section payment-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("payment")}
              >
                <h3>
                  <FontAwesomeIcon icon={faMoneyBillWave} /> Payment Information
                </h3>
                <span className="toggle-icon">
                  {expandedSections.payment ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.payment && (
                <div className="payment-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Method:</span>
                      <span className="detail-value">
                        {getPaymentMethodLabel(order.payment.method)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span
                        className={`payment-status-badge ${order.payment.status}`}
                      >
                        {getPaymentStatusLabel(order.payment.status)}
                      </span>
                    </div>
                    {order.payment.transactionId && (
                      <div className="detail-item">
                        <span className="detail-label">Transaction ID:</span>
                        <span className="detail-value">
                          {order.payment.transactionId}
                        </span>
                      </div>
                    )}
                    {order.payment.mobileMoneyNumber && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faMobileAlt} /> Mobile Money:
                        </span>
                        <span className="detail-value">
                          {order.payment.mobileMoneyNumber}
                        </span>
                      </div>
                    )}
                    {order.payment.bankReference && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faBuilding} /> Bank Ref:
                        </span>
                        <span className="detail-value">
                          {order.payment.bankReference}
                        </span>
                      </div>
                    )}
                    {order.payment.depositAmount?.usd > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Deposit:</span>
                        <span className="detail-value">
                          ${order.payment.depositAmount.usd.toFixed(2)}
                          {order.payment.depositAmount.sll > 0 &&
                            ` (${order.payment.depositAmount.sll.toLocaleString()} SLL)`}
                        </span>
                      </div>
                    )}
                    {order.payment.balanceDue?.usd > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Balance Due:</span>
                        <span className="detail-value balance-due">
                          ${order.payment.balanceDue.usd.toFixed(2)}
                          {order.payment.balanceDue.sll > 0 &&
                            ` (${order.payment.balanceDue.sll.toLocaleString()} SLL)`}
                        </span>
                      </div>
                    )}
                    {order.payment.paidAt && (
                      <div className="detail-item">
                        <span className="detail-label">Paid At:</span>
                        <span className="detail-value">
                          {formatDate(order.payment.paidAt)}
                        </span>
                      </div>
                    )}
                    {order.farmerDetails?.creditTerms?.isCreditOrder && (
                      <div className="detail-item">
                        <span className="detail-label">Credit Due:</span>
                        <span className="detail-value">
                          {formatDate(order.farmerDetails.creditTerms.dueDate)}
                        </span>
                      </div>
                    )}
                    {order.payment.currency === "SLL" && (
                      <div className="detail-item">
                        <span className="detail-label">Currency:</span>
                        <span className="detail-value">
                          <span className="currency-tag">🇸🇱 SLL</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============================================
              SHIPPING INFORMATION
              ============================================ */}
          {order.shipping && (
            <div className="detail-section shipping-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("shipping")}
              >
                <h3>
                  <FontAwesomeIcon icon={faTruck} /> Shipping Information
                </h3>
                <span className="toggle-icon">
                  {expandedSections.shipping ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.shipping && (
                <div className="shipping-details">
                  <div className="detail-grid">
                    {order.shipping.trackingNumber && (
                      <div className="detail-item">
                        <span className="detail-label">Tracking Number:</span>
                        <span className="detail-value tracking-number">
                          {order.shipping.trackingNumber}
                        </span>
                      </div>
                    )}
                    {order.shipping.carrier && (
                      <div className="detail-item">
                        <span className="detail-label">Carrier:</span>
                        <span className="detail-value">
                          {order.shipping.carrier}
                        </span>
                      </div>
                    )}
                    {order.shipping.estimatedDelivery && (
                      <div className="detail-item">
                        <span className="detail-label">Est. Delivery:</span>
                        <span className="detail-value">
                          {formatDate(order.shipping.estimatedDelivery)}
                        </span>
                      </div>
                    )}
                    {order.shipping.actualDelivery && (
                      <div className="detail-item">
                        <span className="detail-label">Actual Delivery:</span>
                        <span className="detail-value">
                          {formatDate(order.shipping.actualDelivery)}
                        </span>
                      </div>
                    )}
                    {order.shipping.shippingCost?.usd > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Shipping Cost:</span>
                        <span className="detail-value">
                          ${order.shipping.shippingCost.usd.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {order.bulkOrderDetails?.isBulk && (
                      <div className="detail-item">
                        <span className="detail-label">Bulk Details:</span>
                        <span className="detail-value">
                          <FontAwesomeIcon icon={faBoxes} />{" "}
                          {order.bulkOrderDetails.palletsCount || 0} pallets,
                          <FontAwesomeIcon icon={faWeightScale} />{" "}
                          {order.bulkOrderDetails.weightInKg || 0} kg
                          {order.bulkOrderDetails.specialHandling && (
                            <>
                              {" "}
                              <FontAwesomeIcon icon={faInfoCircle} />{" "}
                              {order.bulkOrderDetails.specialHandling}
                            </>
                          )}
                        </span>
                      </div>
                    )}
                    {order.shipping.isBulkShipment && (
                      <div className="detail-item">
                        <span className="detail-label">Container:</span>
                        <span className="detail-value">
                          {order.shipping.containerNumber || "N/A"}
                          {order.shipping.vesselName &&
                            ` (${order.shipping.vesselName})`}
                        </span>
                      </div>
                    )}
                    {order.shipping.portOfLoading && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faAnchor} /> Port of Loading:
                        </span>
                        <span className="detail-value">
                          {order.shipping.portOfLoading}
                          {order.shipping.portOfLoading ===
                            SUPPLIER.portLoading && (
                            <span className="default-tag">Default</span>
                          )}
                        </span>
                      </div>
                    )}
                    {order.shipping.portOfDischarge && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faAnchor} /> Port of Discharge:
                        </span>
                        <span className="detail-value">
                          {order.shipping.portOfDischarge}
                          {order.shipping.portOfDischarge ===
                            SUPPLIER.portDischarge && (
                            <span className="default-tag">Default</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============================================
              FARMER DETAILS
              ============================================ */}
          {order.farmerDetails && (
            <div className="detail-section farmer-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("farmer")}
              >
                <h3>
                  <FontAwesomeIcon icon={faTractor} /> Farmer Details
                </h3>
                <span className="toggle-icon">
                  {expandedSections.farmer ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.farmer && (
                <div className="farmer-details">
                  <div className="detail-grid">
                    {order.farmerDetails.farmName && (
                      <div className="detail-item">
                        <span className="detail-label">Farm Name:</span>
                        <span className="detail-value">
                          {order.farmerDetails.farmName}
                        </span>
                      </div>
                    )}
                    {order.farmerDetails.farmSize && (
                      <div className="detail-item">
                        <span className="detail-label">Farm Size:</span>
                        <span className="detail-value">
                          {order.farmerDetails.farmSize
                            .charAt(0)
                            .toUpperCase() +
                            order.farmerDetails.farmSize.slice(1)}
                        </span>
                      </div>
                    )}
                    {order.farmerDetails.preferredDeliveryDay && (
                      <div className="detail-item">
                        <span className="detail-label">
                          Preferred Delivery:
                        </span>
                        <span className="detail-value">
                          {order.farmerDetails.preferredDeliveryDay}
                        </span>
                      </div>
                    )}
                    {order.farmerDetails.deliveryInstructions && (
                      <div className="detail-item">
                        <span className="detail-label">Instructions:</span>
                        <span className="detail-value">
                          {order.farmerDetails.deliveryInstructions}
                        </span>
                      </div>
                    )}
                    {order.farmerDetails.creditTerms?.isCreditOrder && (
                      <div className="detail-item">
                        <span className="detail-label">Credit Approved:</span>
                        <span className="detail-value">
                          {order.farmerDetails.creditTerms.approvedAt
                            ? formatDate(
                                order.farmerDetails.creditTerms.approvedAt,
                              )
                            : "Pending"}
                        </span>
                      </div>
                    )}
                    {order.farmerDetails.supplierName && (
                      <div className="detail-item">
                        <span className="detail-label">Supplier:</span>
                        <span className="detail-value">
                          <FontAwesomeIcon icon={faShip} />{" "}
                          {order.farmerDetails.supplierName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============================================
              NOTES
              ============================================ */}
          {(order.customerNotes || order.adminNotes) && (
            <div className="detail-section notes-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("notes")}
              >
                <h3>
                  <FontAwesomeIcon icon={faClipboard} /> Notes
                </h3>
                <span className="toggle-icon">
                  {expandedSections.notes ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.notes && (
                <div className="notes-details">
                  {order.customerNotes && (
                    <div className="note-item customer-note">
                      <span className="note-label">Customer:</span>
                      <p>{order.customerNotes}</p>
                    </div>
                  )}
                  {order.adminNotes && (
                    <div className="note-item admin-note">
                      <span className="note-label">Admin:</span>
                      <p>{order.adminNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {order.customerWhatsapp && (
            <a
              href={`https://wa.me/${order.customerWhatsapp}?text=Hi%20${encodeURIComponent(order.userName || order.customerName || "")}%2C%20your%20order%20${encodeURIComponent(order.orderNumber || "")}%20is%20currently%20${encodeURIComponent(getStatusLabel(order.status))}.%20For%20any%20questions%2C%20please%20contact%20Kenali%20Enterprise.`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <FontAwesomeIcon icon={faWhatsappBrand} /> Contact Customer
            </a>
          )}
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        <style>{`
          .modal-overlay {
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
            display: flex; align-items: center; justify-content: center;
            z-index: 1000; padding: 1rem;
          }
          .modal-content {
            background: #fff; border-radius: 12px; max-width: 1000px;
            width: 100%; max-height: 95vh; display: flex;
            flex-direction: column; overflow: hidden;
            box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          }
          .modal-content.large { max-width: 1000px; }
          .modal-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: 1.25rem 1.5rem; border-bottom: 1px solid #e5e5e5;
            flex-shrink: 0; flex-wrap: wrap; gap: 0.5rem;
          }
          .modal-header h2 {
            color: #006b2e; font-size: 1.1rem; margin: 0;
            display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          }
          .modal-header h2 svg { color: #d9a321; }
          .badge {
            font-size: 0.6rem; padding: 0.15rem 0.5rem; border-radius: 10px;
            font-weight: 500; display: inline-flex; align-items: center; gap: 0.25rem;
          }
          .badge.bulk { background: #ede9fe; color: #7c3aed; }
          .badge.credit { background: #fef3c7; color: #d97706; }
          .badge.malaysia { background: #dcfce7; color: #16a34a; }
          .badge.source { background: #dbeafe; color: #2563eb; }
          .close-btn {
            background: none; border: none; font-size: 1.25rem;
            cursor: pointer; color: #6b7280; padding: 0.25rem;
            border-radius: 50%; transition: all 0.3s ease;
          }
          .close-btn:hover { background: #f0f0f0; color: #1f1f1f; }

          .modal-body { padding: 1.5rem; overflow-y: auto; flex: 1; }
          .modal-footer {
            padding: 1rem 1.5rem; border-top: 1px solid #e5e5e5;
            display: flex; justify-content: space-between; align-items: center;
            flex-wrap: wrap; gap: 0.5rem; flex-shrink: 0;
          }
          .btn-secondary {
            padding: 0.4rem 1.2rem; background: #f0f0f0;
            color: #1f1f1f; border: 1px solid #e5e5e5;
            border-radius: 6px; cursor: pointer; font-weight: 500;
          }
          .btn-secondary:hover { background: #e5e5e5; }
          .btn-whatsapp {
            padding: 0.4rem 1.2rem; background: #25d366; color: #fff;
            border: none; border-radius: 6px; cursor: pointer;
            font-weight: 600; text-decoration: none;
            display: inline-flex; align-items: center; gap: 0.5rem;
            transition: all 0.3s ease;
          }
          .btn-whatsapp:hover { background: #1da851; transform: translateY(-2px); }

          .status-section {
            padding: 1rem; border-radius: 8px; margin-bottom: 1.25rem;
          }
          .status-header {
            display: flex; justify-content: space-between;
            align-items: center; flex-wrap: wrap; gap: 0.5rem;
          }
          .status-display {
            display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          }
          .status-display svg { font-size: 1.1rem; }
          .status-label { font-weight: 600; font-size: 0.95rem; }
          .order-date, .order-age, .origin-tag {
            font-size: 0.8rem; color: #5a5a5a;
            display: flex; align-items: center; gap: 0.25rem;
          }
          .origin-tag { background: #dcfce7; padding: 0.1rem 0.4rem; border-radius: 10px; color: #16a34a; }
          .btn-edit {
            padding: 0.3rem 0.8rem; background: #d9a321; color: #fff;
            border: none; border-radius: 4px; cursor: pointer;
            font-size: 0.75rem; display: flex; align-items: center; gap: 0.25rem;
          }
          .btn-edit:hover { background: #b8860b; }
          .btn-cancel {
            padding: 0.3rem 0.8rem; background: #f0f0f0; color: #1f1f1f;
            border: 1px solid #e5e5e5; border-radius: 4px; cursor: pointer;
            font-size: 0.75rem; display: flex; align-items: center; gap: 0.25rem;
          }
          .btn-cancel:hover { background: #e5e5e5; }

          .status-update-form {
            margin-top: 0.75rem; padding-top: 0.75rem;
            border-top: 1px solid #e5e5e5;
          }
          .form-row { display: grid; grid-template-columns: 1fr; gap: 0.75rem; }
          .form-group { display: flex; flex-direction: column; gap: 0.25rem; }
          .form-group label { font-weight: 500; font-size: 0.8rem; }
          .form-group select {
            padding: 0.4rem 0.6rem; border: 1px solid #e5e5e5;
            border-radius: 4px; font-size: 0.8rem; width: 100%;
          }
          .form-group select:focus { outline: none; border-color: #d9a321; }
          .form-actions { margin-top: 0.5rem; }
          .btn-primary {
            padding: 0.4rem 1.2rem; background: #d9a321; color: #fff;
            border: none; border-radius: 4px; cursor: pointer;
            font-weight: 500; display: inline-flex; align-items: center; gap: 0.25rem;
          }
          .btn-primary:hover:not(:disabled) { background: #b8860b; }
          .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

          .detail-section {
            margin-bottom: 1rem; padding-bottom: 1rem;
            border-bottom: 1px solid #f0f0f0;
          }
          .detail-section:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
          .supplier-section { border-left: 4px solid #d9a321; padding-left: 1rem; }
          .payment-section { border-left: 4px solid #10b981; padding-left: 1rem; }
          .shipping-section { border-left: 4px solid #3b82f6; padding-left: 1rem; }
          .farmer-section { border-left: 4px solid #8b5cf6; padding-left: 1rem; }

          .section-header-clickable {
            display: flex; justify-content: space-between;
            align-items: center; cursor: pointer;
            padding: 0.25rem 0;
          }
          .section-header-clickable:hover { opacity: 0.7; }
          .section-header-clickable h3 {
            color: #006b2e; font-size: 0.95rem; margin: 0;
            display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          }
          .section-header-clickable h3 svg { color: #d9a321; }
          .toggle-icon { color: #5a5a5a; font-size: 0.7rem; }

          .detail-grid {
            display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;
            padding-top: 0.5rem;
          }
          .detail-item {
            display: flex; flex-direction: column;
            padding: 0.2rem 0;
          }
          .detail-label { font-size: 0.65rem; color: #9ca3af; }
          .detail-value {
            font-size: 0.85rem; color: #1f1f1f;
            display: flex; align-items: center; gap: 0.25rem; flex-wrap: wrap;
          }

          .supplier-info-card {
            background: linear-gradient(135deg, #006B2E, #014421);
            padding: 0.75rem 1.25rem; border-radius: 12px;
            display: flex; align-items: center; gap: 1rem;
            color: #ffffff; margin-bottom: 1rem;
          }
          .supplier-info-card .supplier-icon {
            font-size: 1.5rem; color: #D9A321;
          }
          .supplier-info-card .supplier-info { flex: 1; }
          .supplier-info-card .supplier-info strong {
            font-size: 0.875rem; font-family: "Montserrat", sans-serif;
          }
          .supplier-info-card .supplier-info span {
            font-size: 0.65rem; opacity: 0.8; display: block;
          }
          .supplier-info-card .supplier-info span svg { margin: 0 0.25rem; }
          .supplier-badge {
            font-size: 0.6rem; background: rgba(217,163,33,0.2);
            color: #D9A321; padding: 0.15rem 0.6rem; border-radius: 12px;
            display: inline-flex; align-items: center; gap: 0.25rem;
          }

          .origin-tag, .country-tag { font-size: 0.7rem; }
          .default-tag {
            font-size: 0.55rem; padding: 0.1rem 0.3rem;
            border-radius: 8px; background: #f0f0f0; color: #5a5a5a;
          }
          .destination-tag { font-size: 0.75rem; }
          .currency-tag { font-size: 0.65rem; background: #f0f9ff; color: #3b82f6; padding: 0.1rem 0.4rem; border-radius: 8px; }

          .phone-link { color: #006B2E; text-decoration: none; }
          .phone-link:hover { text-decoration: underline; }
          .whatsapp-link {
            color: #25d366; text-decoration: none;
            display: inline-flex; align-items: center; gap: 0.25rem;
            transition: all 0.3s ease;
          }
          .whatsapp-link:hover { transform: scale(1.05); }

          .user-type-badge {
            font-size: 0.7rem; padding: 0.1rem 0.5rem; border-radius: 10px;
          }
          .user-type-badge.farmer { background: #dcfce7; color: #16a34a; }
          .user-type-badge.customer { background: #dbeafe; color: #2563eb; }

          .payment-status-badge {
            font-size: 0.7rem; padding: 0.1rem 0.5rem; border-radius: 10px;
          }
          .payment-status-badge.pending { background: #fef3c7; color: #d97706; }
          .payment-status-badge.processing { background: #dbeafe; color: #2563eb; }
          .payment-status-badge.paid { background: #dcfce7; color: #16a34a; }
          .payment-status-badge.failed { background: #fee2e2; color: #dc2626; }
          .payment-status-badge.refunded { background: #f3f4f6; color: #6b7280; }
          .payment-status-badge.deposit_received { background: #ede9fe; color: #7c3aed; }

          .balance-due { color: #d9a321; font-weight: 600; }

          .items-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
          .items-table th {
            background: #f8f9fa; padding: 0.3rem 0.5rem;
            text-align: left; font-weight: 600; color: #5a5a5a;
            border-bottom: 1px solid #e5e5e5;
          }
          .items-table td {
            padding: 0.3rem 0.5rem; border-bottom: 1px solid #f0f0f0;
          }
          .items-table tfoot td { font-weight: 600; border-top: 2px solid #e5e5e5; }
          .items-table .discount { color: #16a34a; }
          .items-table .grand-total { font-size: 1rem; }
          .items-table .grand-total .total-value { color: #d9a321; }

          .product-info {
            display: flex; align-items: center; gap: 0.25rem; flex-wrap: wrap;
          }
          .item-thumb {
            width: 32px; height: 32px; object-fit: cover; border-radius: 4px;
          }
          .item-name { font-weight: 500; }
          .tier-badge {
            font-size: 0.55rem; padding: 0.1rem 0.3rem;
            border-radius: 8px; background: #f0f0f0; color: #5a5a5a;
          }
          .discount-badge {
            font-size: 0.6rem; padding: 0.1rem 0.3rem;
            border-radius: 8px; background: #dcfce7; color: #16a34a;
          }

          .item-count, .timeline-count, .origin-summary {
            font-size: 0.65rem; color: #5a5a5a; font-weight: 400;
          }
          .origin-summary {
            background: #dcfce7; color: #16a34a; padding: 0.1rem 0.4rem;
            border-radius: 10px;
          }

          .timeline { position: relative; padding-left: 1.5rem; }
          .timeline::before {
            content: ''; position: absolute; left: 6px; top: 0; bottom: 0;
            width: 2px; background: #e5e5e5;
          }
          .timeline-item {
            position: relative; padding-bottom: 0.75rem;
            display: flex; gap: 0.75rem;
          }
          .timeline-item:last-child { padding-bottom: 0; }
          .timeline-dot {
            position: absolute; left: -1.25rem; top: 2px;
            width: 12px; height: 12px; border-radius: 50%;
            border: 2px solid #fff; flex-shrink: 0;
          }
          .timeline-content { flex: 1; }
          .timeline-status { font-weight: 600; font-size: 0.85rem; }
          .updated-by { font-weight: 400; font-size: 0.65rem; color: #9ca3af; margin-left: 0.25rem; }
          .timeline-description { font-size: 0.75rem; color: #5a5a5a; }
          .timeline-date { font-size: 0.65rem; color: #9ca3af; }

          .note-item { padding: 0.5rem 0.75rem; border-radius: 6px; margin-bottom: 0.5rem; }
          .note-item:last-child { margin-bottom: 0; }
          .note-item.customer-note { background: #f0f9ff; border-left: 3px solid #3b82f6; }
          .note-item.admin-note { background: #fef3c7; border-left: 3px solid #d9a321; }
          .note-label { font-weight: 600; font-size: 0.65rem; color: #5a5a5a; }
          .note-item p { margin: 0.25rem 0 0; font-size: 0.8rem; }

          .no-data { color: #9ca3af; font-style: italic; }

          .table-responsive { overflow-x: auto; }

          @media (max-width: 768px) {
            .detail-grid { grid-template-columns: 1fr; }
            .modal-content.large { max-width: 100%; margin: 0.5rem; }
            .modal-header h2 { font-size: 0.95rem; }
            .status-header { flex-direction: column; align-items: stretch; }
            .status-display { flex-direction: column; align-items: flex-start; }
            .modal-footer { flex-direction: column; align-items: stretch; }
            .btn-whatsapp { justify-content: center; }
            .supplier-info-card { flex-wrap: wrap; justify-content: center; text-align: center; }
            .items-table { font-size: 0.7rem; }
            .items-table th, .items-table td { padding: 0.2rem 0.3rem; }
          }
          @media (max-width: 480px) {
            .modal-body { padding: 1rem; }
            .detail-section { padding: 0.5rem 0; }
            .badge { font-size: 0.5rem; padding: 0.1rem 0.3rem; }
            .items-table .grand-total { font-size: 0.9rem; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
